import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { get } from "lodash";

import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

import { REQUIRED_MARK } from "./fieldClasses";

const FormInput = ({
  name,
  label,
  required,
  type = "text",
  placeholder,
  prefix,
  suffix,
  disabled,
  id,
  onValueChange,
  ...rest
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const inputProps = {
          // Fields like "name" are business data, not credentials — keep
          // password managers (1Password, LastPass, Bitwarden) from offering
          // to fill or save them.
          autoComplete: "off",
          "data-1p-ignore": true,
          "data-lpignore": "true",
          "data-bwignore": true,
          "data-form-type": "other",
          ...rest,
          id: inputId,
          type,
          placeholder,
          disabled,
          "aria-required": required || undefined,
          "aria-invalid": error ? true : undefined,
          name: field.name,
          value: field.value ?? "",
          onBlur: field.onBlur,
          onChange: e => {
            field.onChange(e.target.value);
            onValueChange?.(e.target.value);
          },
        };
        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            {prefix || suffix ? (
              <InputGroup>
                {prefix && (
                  <InputGroupAddon align="inline-start">
                    {prefix}
                  </InputGroupAddon>
                )}
                <InputGroupInput {...inputProps} />
                {suffix && (
                  <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon>
                )}
              </InputGroup>
            ) : (
              <Input {...inputProps} />
            )}
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  onValueChange: PropTypes.func,
};

export default FormInput;
